import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReelHost from "../components/ReelHost/ReelHost";
import LanguageInfoPanel from "../components/LanguageInfoPanel/LanguageInfoPanel";
import { LanguageContext } from "../contexts/LanguageContext/LanguageContext";
import "../styles/colors.css";
import "./Home.css";

import { Globe, Brain, PlayCircle, CircleDollarSign } from "lucide-react";

const Home = () => {
  const navigate = useNavigate();
  const { languages } = useContext(LanguageContext);

  const handleStartClick = () => {
    navigate("/initial-translation-order");
  };

  const handleLanguageSelectionChanged = ({
    id,
    name,
    number_of_speakers,
    location,
    code,
    flag,
    type,
  }) => {
    console.log(`${type} Language Selected in Home:`, {
      id,
      name,
      number_of_speakers,
      location,
      code,
      flag,
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>AI Video Translation & Localization – LingoLab</title>
        <meta
          name="description"
          content="LingoLab uses advanced AI to translate and localize your videos into 40+ languages, preserving the original voice and syncing lips for a native look. Expand your global reach, boost CPM, and grow your audience with seamless, affordable video translation."
        />
        <meta
          name="keywords"
          content="AI video translation, multilingual video, video localization, lip sync AI, voice preservation, global video reach, affordable translation, native video dubbing"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://lingolab.ca/" />
        <meta
          property="og:title"
          content="LingoLab | AI-Powered Video Translation & Localization"
        />
        <meta
          property="og:description"
          content="Reach global audiences with natural-looking video translations using AI voice preservation and lip-sync technology. Translate, localize, and monetize your content worldwide."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/" />
        {/* --- Twitter Card tags --- */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingoLabAI" />
        <meta
          name="twitter:title"
          content="LingoLab | AI-Powered Video Translation & Localization"
        />
        <meta
          name="twitter:description"
          content="Reach global audiences with natural-looking video translations using AI voice preservation and lip-sync technology. Translate, localize, and monetize your content worldwide."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>

      <div
        style={{ backgroundColor: "var(--blue-900)" }}
        className="text-gray-100 text-center py-6 text-xl sm:text-2xl font-semibold"
      >
        AI-Powered Video Translation
      </div>

      <div className="w-full bg-gray-100 border-b border-gray-200 shadow-sm">
        <div className="w-full mx-auto">
          <ReelHost configPath="/HomeReelConfigMulti/reelConfigMulti.json" />
        </div>
      </div>

      <div className="px-4 sm:px-6 max-w-7xl mx-auto py-4 sm:py-6">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-900 text-center mb-8 sm:mb-10">
          Why Choose LingoLab Translation Services?
        </h2>

        <div className="why-choose-section">
          <div className="language-selection-wrapper">
            <div className="bg-white p-4 sm:p-6 border border-gray-100 rounded-lg shadow-sm">
              <h3 className="text-lg sm:text-xl font-bold text-blue-900 flex items-center gap-2 justify-center">
                <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                Global Reach, Real Revenue
              </h3>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-700 max-w-2xl mx-auto">
                Maximize your video’s potential. LingoLab translates your
                content into multiple languages with native-level precision,
                increasing reach and CPM.
              </p>
              <div className="mt-4 sm:mt-6 bg-white p-4 sm:p-6 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-sm sm:text-base font-medium text-gray-700 mb-3 sm:mb-4">
                  Choose from {languages.length} languages to see how much more
                  your video could earn.
                </p>
                <LanguageInfoPanel
                  statsTextStorageKey="homeTranslationStatsState"
                  disableHide={true}
                  translationStatsOnly={false}
                  onLanguageSelectionChanged={handleLanguageSelectionChanged}
                  showRevenueEstimate={true}
                  baseRevenue={1000}
                  fromStorageKey="homeFromVisibleLanguages"
                  toStorageKey="homeToVisibleLanguages"
                  selectionRequired={false}
                />
              </div>
            </div>
          </div>

          <div className="why-choose-content">
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-white p-4 sm:p-6 border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg sm:text-xl font-bold text-blue-900 flex items-center gap-2 justify-center">
                  <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                  AI That Keeps It Real
                </h3>
                <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-700">
                  Maintain the original voice, personality, and timing with AI
                  that mimics your tone and syncs lips to match every translated
                  language.
                </p>
              </div>

              <div className="bg-white p-4 sm:p-6 border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg sm:text-xl font-bold text-blue-900 flex items-center gap-2 justify-center">
                  <CircleDollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                  Affordable Global Growth
                </h3>
                <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-700">
                  Get enterprise-level video localization at a fraction of the
                  cost—no hidden fees, just transparent pricing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 max-w-7xl mx-auto py-4 sm:py-6">
        <div className="bg-white shadow-md rounded-xl border border-gray-100 p-6 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          <div className="md:col-span-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-blue-900 flex items-center gap-2">
              <PlayCircle className="w-7 h-7 sm:w-8 sm:h-8 text-red-500" />
              How It Works
            </h2>
            <h3 className="text-lg sm:text-xl font-bold text-blue-700 mt-2 sm:mt-3">
              A Seamless 3-Step Journey
            </h3>
            <div className="mt-6 sm:mt-8 space-y-6 sm:space-y-8 text-gray-700">
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-blue-900 relative pl-6">
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 text-red-500">
                    •
                  </span>
                  Submit Your Video Link
                </h4>
                <p className="mt-1 sm:mt-2 text-sm sm:text-base">
                  Upload or link to your video in any language or format—we’ll
                  handle the rest.
                </p>
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-blue-900 relative pl-6">
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 text-red-500">
                    •
                  </span>
                  Receive a Custom Quote
                </h4>
                <p className="mt-1 sm:mt-2 text-sm sm:text-base">
                  Get a transparent, tailored price based on length, complexity,
                  and target languages.
                </p>
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-blue-900 relative pl-6">
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 text-red-500">
                    •
                  </span>
                  Download and Share
                </h4>
                <p className="mt-1 sm:mt-2 text-sm sm:text-base">
                  Receive your fully translated, lip-synced, native-sounding
                  video—ready for global impact.
                </p>
              </div>
            </div>
          </div>
          <div className="md:col-span-1 flex items-center justify-center">
            <button
              className="bg-red-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-red-600 shadow-sm hover:shadow-md transition-all duration-200 w-full sm:w-auto"
              onClick={handleStartClick}
            >
              Translate Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
